// Pure data — no icon component values, so this stays server-safe.

export const QUICK_ANSWER =
  "Choose Proxidize if you need premium mobile proxies — Bright Data's current public pages don't list a mobile proxy category. Choose Bright Data if you need a broad enterprise web-data platform: ISP proxies you keep for life, datacenter proxies at scale, or bundled scraper APIs and datasets. For residential proxies, compare both directly — Proxidize is a flat $1/GB versus Bright Data's $4.00/GB pay-as-you-go rate (down to $2.50/GB only on their largest committed tier).";

export const KEY_TAKEAWAYS = [
  "Bright Data doesn't publicly sell mobile proxies. Their proxy-types page lists only Residential, ISP, and Datacenter.",
  "Proxidize residential runs about a quarter of Bright Data's pay-as-you-go rate. $1/GB versus $4.00/GB at typical volumes.",
  "Bright Data covers more proxy types overall. ISP and datacenter proxies, plus scraper APIs and ready-made datasets that Proxidize doesn't offer yet.",
  "Both providers are enterprise-grade on trust and security. SOC 2 Type II and ISO 27001 certified, with comparable Trustpilot and G2 ratings.",
  "Proxidize setup is self-serve end to end. Free sign-up, no credit card, working credentials in under a minute.",
  "Neither provider is the universal answer. Mobile IPs, flat residential pricing, and fast self-serve setup favor Proxidize; ISP/datacenter proxies, the largest residential pool, and bundled scraping APIs favor Bright Data.",
];

export interface VerdictCard {
  provider: "proxidize" | "brightdata";
  title: string;
  points: string[];
}

export const VERDICT_CARDS: VerdictCard[] = [
  {
    provider: "proxidize",
    title: "Choose Proxidize if you need",
    points: [
      "Premium pooled mobile IPs across multiple countries — a category Bright Data's current public pages don't list",
      "Flat $2/GB mobile pricing, or $59/proxy/month with unlimited bandwidth",
      "Residential proxies in 195+ countries at a flat $1/GB with rollover bandwidth",
      "City-level targeting for mobile, city/ISP targeting for residential",
      "Self-serve setup: credentials in under a minute, no sales call",
      "Sticky or random session modes, manual rotation, and an API rotation URL",
      "Free sign-up without a credit card",
    ],
  },
  {
    provider: "brightdata",
    title: "Choose Bright Data if you need",
    points: [
      "A broad enterprise web-data platform beyond proxies: scraper APIs, SERP APIs, ready-made datasets",
      "ISP (static residential) proxies you keep for life — 1.3M+ IPs, from $1.3/IP",
      "Datacenter proxies at scale — 1.3M+ IPs, from $0.90/IP or $0.42/GB",
      "The largest published residential pool (400M+ IPs) across 195 countries",
      "Enterprise procurement, SLAs, and a dedicated account manager",
    ],
  },
];

export interface ComparisonRow {
  feature: string;
  proxidize: string | boolean;
  brightdata: string | boolean;
}

export const COMPARISON_ROWS: ComparisonRow[] = [
  {
    feature: "Best for",
    proxidize: "Mobile IPs, self-serve simplicity",
    brightdata: "Enterprise web data: residential, ISP, datacenter, scraper APIs",
  },
  { feature: "Mobile proxies offered", proxidize: true, brightdata: false },
  {
    feature: "Mobile pricing",
    proxidize: "Flat $2/GB, or $59/proxy/month",
    brightdata: "Not publicly listed — no mobile product to price",
  },
  {
    feature: "Mobile targeting",
    proxidize: "City + ASN",
    brightdata: "N/A — no mobile product",
  },
  {
    feature: "Residential network",
    proxidize: "Millions of IPs, 195+ countries, ethically sourced",
    brightdata: "400M+ IPs, 195 countries",
  },
  {
    feature: "Residential pricing",
    proxidize: "Flat $1/GB (plans from 25 GB to 10,000 GB)",
    brightdata: "$4.00/GB pay-as-you-go, down to $2.50/GB at the 798 GB committed tier",
  },
  {
    feature: "100 GB residential plan",
    proxidize: "$100/mo ($1.00/GB)",
    brightdata: "~$400/mo pay-as-you-go ($4.00/GB) — no committed tier covers 100 GB",
  },
  {
    feature: "Rollover of unused residential bandwidth",
    proxidize: true,
    brightdata: "See their terms",
  },
  {
    feature: "ISP (static residential) proxies",
    proxidize: "Not yet offered",
    brightdata: "1.3M+ IPs, from $1.3/IP, keep the IP for life",
  },
  {
    feature: "Datacenter proxies",
    proxidize: "Not yet offered",
    brightdata: "1.3M+ IPs, from $0.90/IP or $0.42/GB",
  },
  { feature: "Scraper APIs / ready-made datasets", proxidize: false, brightdata: true },
  {
    feature: "Sticky sessions",
    proxidize: "Sticky or random per access point",
    brightdata: "Sticky and rotating sessions on the proxy network",
  },
  {
    feature: "Rotation control",
    proxidize: "Custom interval (min 1 min), manual, API rotation URL",
    brightdata: "Automatic rotation",
  },
  {
    feature: "Protocols",
    proxidize: "HTTP(S), SOCKS5, HTTP/3 + QUIC",
    brightdata: "HTTP(S), SOCKS5",
  },
  {
    feature: "Self-serve signup",
    proxidize: true,
    brightdata: "Self-serve for standard plans, sales-assisted for enterprise",
  },
  {
    feature: "Free sign-up",
    proxidize: "No credit card required",
    brightdata: "Free trial available",
  },
  {
    feature: "Security certifications",
    proxidize: "SOC 2 Type II, ISO 27001",
    brightdata: "SOC 2, ISO 27001",
  },
  { feature: "Trustpilot rating", proxidize: "4.8/5", brightdata: "4.4/5 (~970 reviews)" },
  { feature: "G2 rating", proxidize: "4.7/5", brightdata: "4.6/5 (284 reviews)" },
  {
    feature: "Money-back guarantee",
    proxidize: "30 days (usage & policy limits)",
    brightdata: "See their terms",
  },
];

export interface UseCaseFit {
  useCase: string;
  fit: "Proxidize" | "Bright Data" | "Both";
  reason: string;
}

export const USE_CASE_FITS: UseCaseFit[] = [
  {
    useCase: "Need premium pooled mobile IPs",
    fit: "Proxidize",
    reason:
      "Bright Data's current public proxy pages don't list a mobile proxy category to compare against.",
  },
  {
    useCase: "Global residential scraping at enterprise scale",
    fit: "Both",
    reason:
      "Bright Data's 400M+ IP pool is larger; Proxidize's flat $1/GB rate is cheaper at most volumes. Compare on price and targeting for your specific footprint.",
  },
  {
    useCase: "Long-lived static IPs you keep indefinitely",
    fit: "Bright Data",
    reason:
      "Bright Data ISP proxies let you keep the same IP for life; Proxidize doesn't publicly offer ISP proxies yet.",
  },
  {
    useCase: "High-volume datacenter scraping",
    fit: "Bright Data",
    reason:
      "Bright Data publishes dedicated datacenter proxy pricing; Proxidize doesn't currently offer a datacenter product.",
  },
  {
    useCase: "Account workflows needing a stable mobile exit",
    fit: "Proxidize",
    reason:
      "Per-proxy mobile plans give you a consistent mobile IP you control, with unlimited bandwidth.",
  },
  {
    useCase: "One vendor for proxies plus scraping APIs and datasets",
    fit: "Bright Data",
    reason:
      "Bright Data bundles proxies with a scraper API, SERP API, and ready-made datasets; Proxidize is proxy-focused.",
  },
  {
    useCase: "Simple self-serve setup without a sales call",
    fit: "Proxidize",
    reason:
      "Standard Proxidize plans are self-serve with credentials ready in under a minute; several Bright Data plans route through their sales team.",
  },
];

export interface MigrationStep {
  title: string;
  description: string;
}

export const MIGRATION_STEPS: MigrationStep[] = [
  {
    title: "Create a free account",
    description: "Sign up for free: no credit card, no sales call.",
  },
  {
    title: "Pick per-GB or per-proxy",
    description:
      "Per-GB gives flexible access to the full mobile pool billed by data. Per-proxy gives you a real mobile IP of your own with unlimited bandwidth.",
  },
  {
    title: "Create an access point",
    description: "Choose a city or random rotation and set sticky or random IP behavior.",
  },
  {
    title: "Copy your credentials",
    description:
      "Credentials are generated live in HTTP or SOCKS5 format, with auto-generated cURL examples and formatted proxy list exports.",
  },
  {
    title: "Test before switching traffic",
    description:
      "Run your existing scraper or browser profile against the new endpoint with a small job and compare success rates side by side.",
  },
  {
    title: "Monitor usage in real time",
    description:
      "The built-in analytics dashboard shows bandwidth trends, top domains, and a live connection log per access point.",
  },
];

export const MIGRATION_CURL = `curl -x "http://USERNAME:PASSWORD@HOST:PORT" https://ipinfo.io`;
